"use client";

import { useEffect, useState } from "react";

export const profileClockRefreshMs = 60_000;

export function formatProfileLocalTimeLabel(
  timezone: string | undefined,
  now: number,
): string | undefined {
  if (!timezone) {
    return undefined;
  }
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(now);
    const hour = parts.find((part) => part.type === "hour")?.value;
    const minute = parts.find((part) => part.type === "minute")?.value;
    const dayPeriod = parts.find((part) => part.type === "dayPeriod")?.value;
    const timeZoneName = parts.find((part) => part.type === "timeZoneName")?.value;
    if (!hour || !minute || !dayPeriod) {
      return undefined;
    }
    const suffix = timeZoneName ? ` (${timeZoneName})` : "";
    return `${hour}:${minute} ${dayPeriod}${suffix}`;
  } catch {
    return undefined;
  }
}

export function useVisibleProfileClockNow(timezone: string | undefined): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!timezone || typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    const refresh = () => setNow(Date.now());
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };

    refresh();
    const intervalId = window.setInterval(refreshWhenVisible, profileClockRefreshMs);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    window.addEventListener("focus", refresh);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.removeEventListener("focus", refresh);
    };
  }, [timezone]);

  return now;
}
